import { Alert, Anchor, Button, Card, Stack, Text, Title } from '@mantine/core';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { CaptureActions } from '../components/CaptureActions.js';
import { OfflineQueueNotice } from '../components/OfflineQueueNotice.js';
import { useT } from '../i18n/useT.js';
import { installPrompt, useInstallState } from '../offline/install.js';
import { MOBILE_APP_PATH } from './MobileAppPage.js';

/**
 * Indstillinger → Mobil-app.
 *
 * Where the phone app is explained and installed, and where its two actions are
 * reachable from the full UI now that they are no longer on Overblik.
 *
 * Under Indstillinger although installing is per-device rather than
 * per-company: the actions on this page save to the active company, and a
 * person looking for "the app" looks among the things they set up, not among
 * the pages they work in.
 */
function InstallCard(): ReactNode {
  const t = useT();
  const state = useInstallState();
  const [prompting, setPrompting] = useState(false);

  async function install(): Promise<void> {
    setPrompting(true);
    try {
      await installPrompt.prompt();
    } catch {
      /*
        The prompt was already spent — Chrome rejects a second use of the same
        event. Nothing to report: `prompt()` released the event before calling
        it, so the card now shows the manual instructions, which are exactly
        what is still true.
      */
    } finally {
      setPrompting(false);
    }
  }

  return (
    <Card withBorder padding="md">
      <Stack gap="sm">
        <Text fw={500}>{t('mobile.installTitle')}</Text>
        <Text size="sm" c="dimmed">
          {t('mobile.installHelp')}
        </Text>

        {state === 'installed' && (
          <Alert color="green" variant="light">
            {t('mobile.installed')}
          </Alert>
        )}

        {state === 'available' && (
          <Button
            onClick={() => {
              void install();
            }}
            loading={prompting}
          >
            {t('mobile.install')}
          </Button>
        )}

        {state === 'manual' && (
          <Text size="sm">
            {t('mobile.installManual', { url: `${window.location.origin}${MOBILE_APP_PATH}` })}
          </Text>
        )}

        <Anchor component={Link} to={MOBILE_APP_PATH} size="sm">
          {t('mobile.open')}
        </Anchor>
      </Stack>
    </Card>
  );
}

export function MobileAppSettingsPage(): ReactNode {
  const t = useT();

  return (
    <Stack gap="lg">
      <Stack gap={4}>
        <Title order={2}>{t('mobile.title')}</Title>
        <Text c="dimmed" size="sm">
          {t('mobile.intro')}
        </Text>
      </Stack>

      <InstallCard />

      <Title order={3}>{t('mobile.actionsTitle')}</Title>

      <OfflineQueueNotice />

      <CaptureActions />
    </Stack>
  );
}
